/*
  This file is part of the project: "Digiziege"
  An app for animal-welfare-oriented goat husbandry and contactless weight estimation.
  GitLab: https://gitlab.rlp.net/proj-wise2526-digiziege/digiziege

  Copyright (c) 2026 Digiziege Team TH-Bingen

  Description:
  Companion app for welfare-oriented goat husbandry featuring contactless weight estimation via camera,
  master data management and health tracking.
  Built as a PWA with a local-first approach to ensure data privacy.

  LICENSE INFORMATION:
  This program is free software: you can redistribute it and/or modify it under the terms of the
  GNU Affero General Public License as published by the Free Software Foundation,
  either version 3 of the License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
  without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
  See the GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License along with this program.
  If not, see <https://www.gnu.org/licenses/>.
*/

import { defineStore } from "pinia";
import { ref } from "vue";
import { temperatureRepository } from "@/services/repositories/temperature.repository.js";

export const useTemperatureStore = defineStore("temperature", () => {
  const temperatures = ref([]);
  const loading = ref(false);
  const error = ref(null);

  async function fetchTemperatures(goatId) {
    loading.value = true;
    error.value = null;
    try {
      const id = Number(goatId);
      if (isNaN(id)) throw new Error("Undefined Goat ID.");
      temperatures.value = await temperatureRepository.findByGoat(id);
    } catch (e) {
      error.value = e.message;
      temperatures.value = [];
    } finally {
      loading.value = false;
    }
  }

  //fetch all data for activities view
  async function fetchAllTemperatures() {
    loading.value = true;
    error.value = null;
    try {
      temperatures.value = await temperatureRepository.findAll();
      console.log(`Fetched ${temperatures.value.length} temperatures`);
    } catch (e) {
      error.value = e.message;
      temperatures.value = [];
    } finally {
      loading.value = false;
    }
  }

  async function addTemperature(goatId, value, measuredAt = null) {
    loading.value = true;
    error.value = null;
    try {
      const id = Number(goatId);
      if (isNaN(id)) throw new Error("Undefined Goat ID.");
      await temperatureRepository.create({ goatId: id, value, measuredAt });
      await fetchTemperatures(id); // refresh
    } catch (e) {
      error.value = e.message;
    } finally {
      loading.value = false;
    }
  }

  async function updateTemperature(id, goatId, value, measuredAt = null) {
    loading.value = true;
    error.value = null;
    try {
      const gId = Number(goatId);
      if (isNaN(gId)) throw new Error("Undefined Goat ID.");
      await temperatureRepository.update(id, { value, measuredAt });
      await fetchTemperatures(gId);
    } catch (e) {
      error.value = e.message;
    } finally {
      loading.value = false;
    }
  }

  async function deleteTemperature(id, goatId) {
    loading.value = true;
    error.value = null;
    try {
      const gId = Number(goatId);
      if (isNaN(gId)) throw new Error("Undefined Goat ID.");
      await temperatureRepository.delete(id);
      await fetchTemperatures(gId);
    } catch (e) {
      error.value = e.message;
    } finally {
      loading.value = false;
    }
  }

  async function getLatestTemperature(goatId) {
    loading.value = true;
    error.value = null;
    try {
      const id = Number(goatId);
      if (isNaN(id)) throw new Error("Undefined Goat ID.");
      return await temperatureRepository.latest(id);
    } catch (e) {
      error.value = e.message;
      return null;
    } finally {
      loading.value = false;
    }
  }

  return {
    temperatures,
    loading,
    error,
    fetchTemperatures,
    fetchAllTemperatures,
    addTemperature,
    updateTemperature,
    deleteTemperature,
    getLatestTemperature
  };
});
